import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { profile as profileApi } from "@/lib/api";
import { SettingsCard, useSaveProfile } from "./primitives";

/**
 * StateSection — state (region) selector plus the "migrate existing
 * scans?" confirmation dialog. Changing the state is a high-impact op
 * (it retunes the entire native/keystone/endangered corpus), so we
 * always confirm intent and offer the migration path explicitly.
 */
export default function StateSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { saveProfile, saving } = useSaveProfile();
  const [states, setStates] = useState([]);
  const [pendingState, setPendingState] = useState(null);

  useEffect(() => {
    profileApi.states()
      .then(({ data }) => setStates(data.states || []))
      .catch(() => setStates([]));
  }, []);

  const onChangeState = (value) => {
    if (!value || value === user?.state) return;
    setPendingState(value);
  };

  const applyStateChange = async (migrate) => {
    const value = pendingState;
    setPendingState(null);
    if (!value) return;
    const data = await saveProfile(
      { state: value, migrate_existing_scans: migrate },
      migrate
        ? t("settings.stateMigrated", { state: value })
        : t("settings.stateSaved", { state: value })
    );
    if (data && migrate && data._migrated_scans > 0) {
      toast.message(t("settings.migrateNote", { n: data._migrated_scans }));
    }
  };

  return (
    <>
      <SettingsCard
        icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}
        title={t("settings.stateTitle")}
        help={t("settings.stateHelp")}
      >
        <Select
          key={user?.state || "Illinois"}
          value={user?.state || "Illinois"}
          onValueChange={onChangeState}
          disabled={saving}
        >
          <SelectTrigger data-testid="state-select" aria-label={t("settings.stateChoose")} className="w-full rounded-full border-[#E8E5DF] bg-cream">
            <SelectValue placeholder={t("settings.stateChoose")} />
          </SelectTrigger>
          <SelectContent>
            {states.map((s) => (
              <SelectItem
                key={s}
                value={s}
                data-testid={`state-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingsCard>

      <AlertDialog open={!!pendingState} onOpenChange={(o) => !o && setPendingState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="state-change-title">
              {t("settings.stateChangeTitle", { state: pendingState || "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.stateChangeDesc", {
                oldState: user?.state || "",
                newState: pendingState || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-2 space-y-2" data-testid="state-change-options">
            <button
              type="button"
              onClick={() => applyStateChange(false)}
              disabled={saving}
              data-testid="state-change-newonly"
              className="w-full rounded-2xl border border-[#E8E5DF] bg-white hover:bg-parchment text-left p-3 disabled:opacity-60 transition"
            >
              <p className="font-medium text-leaf-ink text-sm">{t("settings.stateChangeNewOnlyLabel")}</p>
              <p className="mt-1 text-[12px] text-leaf-mist">
                {t("settings.stateChangeNewOnlyDesc", { oldState: user?.state || "", newState: pendingState || "" })}
              </p>
            </button>
            <button
              type="button"
              onClick={() => applyStateChange(true)}
              disabled={saving}
              data-testid="state-change-migrate"
              className="w-full rounded-2xl border border-leaf bg-leaf/5 hover:bg-leaf/10 text-left p-3 disabled:opacity-60 transition"
            >
              <p className="font-medium text-leaf-ink text-sm">{t("settings.stateChangeMigrateLabel")}</p>
              <p className="mt-1 text-[12px] text-leaf-mist">
                {t("settings.stateChangeMigrateDesc", { oldState: user?.state || "", newState: pendingState || "" })}
              </p>
            </button>
          </div>

          <AlertDialogFooter className="sm:justify-start">
            <AlertDialogCancel data-testid="state-change-cancel" disabled={saving}>
              {t("common.cancel")}
            </AlertDialogCancel>
            {/* AlertDialogAction is required by the component but we drive the actions above. */}
            <AlertDialogAction className="hidden" />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
